import * as fabric from 'fabric'
import { EditorColorType, EditorColoreApiType, EditorObjectType } from '@utils/types'
import {
  applyCornerOnlyControls,
  getContainmentDeltaInAreaSpace,
  getObjectBoundsInAreaSpace,
  shrinkObjectToFitAreaBounds,
  translateObjectInAreaSpace,
} from './fabric-editor-helpers'
import { normalizeHexColor } from './index'
import { getPantoneHex } from './colors'

type HiddenColorBucket = {
  color: string
  objects: any[]
}

type HiddenColorsData = {
  colors: Record<string, HiddenColorBucket>
  lastVisibleTransform?: fabric.TMat2D
}

const STACK_INDEX_PROP = '_hiddenColorsStackIndex'

/**
 * Gestione colori nascosti dei loghi SVG.
 *
 * Non usiamo `visible=false` sui sotto-oggetti perche' Fabric manterrebbe il loro
 * ingombro nel bounding box del gruppo. Invece estraiamo gli elementi del colore
 * spento, li serializziamo in `_hiddenColorsData` e aggiorniamo il gruppo Fabric
 * corrente. In questo modo il bounding box viene ricalcolato da Fabric, ma
 * l'oggetto canvas e il relativo controllo UI restano gli stessi.
 */
const getHiddenData = (obj: any): HiddenColorsData => ({
  colors: obj?._hiddenColorsData?.colors || {},
  lastVisibleTransform: obj?._hiddenColorsData?.lastVisibleTransform,
})

const hasPaintForColor = (obj: any, colorHex: string) => {
  const fill = normalizeHexColor(obj?.originalFill || obj?.fill || '')
  const stroke = normalizeHexColor(obj?.originalStroke || obj?.stroke || '')

  return (
    (fill && fill === colorHex) ||
    (stroke && stroke === colorHex && (!obj?.strokeWidth || obj.strokeWidth > 0))
  )
}

const eachLeafObject = (obj: any, callback: (leaf: any) => void) => {
  if (!obj) return
  if (obj._objects?.length) {
    obj._objects.forEach((child: any) => eachLeafObject(child, callback))
    return
  }
  callback(obj)
}

const objectContainsColor = (obj: any, colorHex: string) => {
  let found = false
  eachLeafObject(obj, (leaf) => {
    if (!found && hasPaintForColor(leaf, colorHex)) found = true
  })
  return found
}

const serializeHiddenObject = (obj: fabric.Object) => obj.toObject()

const ensureStackIndexes = (objects: fabric.Object[]) => {
  objects.forEach((obj: any, index) => {
    // Serve per reinserire i colori ripristinati nella posizione z-index originale.
    // Senza questo dato Fabric li aggiunge in fondo al gruppo e possono finire sopra
    // background o tracciati che in origine stavano davanti.
    if (!Number.isFinite(obj?.[STACK_INDEX_PROP])) {
      obj[STACK_INDEX_PROP] = index
    }
  })
}

const sortByStackIndex = (objects: fabric.Object[]) =>
  [...objects].sort((a: any, b: any) => {
    const aIndex = Number.isFinite(a?.[STACK_INDEX_PROP])
      ? a[STACK_INDEX_PROP]
      : Number.MAX_SAFE_INTEGER
    const bIndex = Number.isFinite(b?.[STACK_INDEX_PROP])
      ? b[STACK_INDEX_PROP]
      : Number.MAX_SAFE_INTEGER
    if (aIndex !== bIndex) return aIndex - bIndex
    return 0
  })

const enlivenSerializedObjects = async (objects: any[] = []) =>
  ((await fabric.util.enlivenObjects(objects)) as fabric.Object[]) || []

export const syncHiddenColorsDataTransform = async (obj: fabric.Object | null | undefined) => {
  const fabricObject = obj as any
  const hiddenData = getHiddenData(fabricObject)
  const hasHiddenObjects = Object.values(hiddenData.colors).some((bucket) => bucket.objects?.length)

  if (!fabricObject || !hasHiddenObjects) return

  const currentMatrix = fabricObject.calcTransformMatrix()
  const previousMatrix = hiddenData.lastVisibleTransform

  // Se il logo viene mosso/scalato/ruotato mentre un colore e' nascosto,
  // gli oggetti serializzati non ricevono eventi Fabric. Applichiamo quindi
  // la matrice delta anche al payload nascosto, cosi' al ripristino rientra
  // nello stesso asset trasformato.
  if (!previousMatrix) {
    fabricObject._hiddenColorsData = {
      ...hiddenData,
      lastVisibleTransform: currentMatrix,
    }
    return
  }

  const deltaMatrix = fabric.util.multiplyTransformMatrices(
    currentMatrix,
    fabric.util.invertTransform(previousMatrix)
  )

  const nextColors: Record<string, HiddenColorBucket> = {}

  for (const [key, bucket] of Object.entries(hiddenData.colors)) {
    const hiddenObjects = await enlivenSerializedObjects(bucket.objects)
    hiddenObjects.forEach((hiddenObject) => {
      fabric.util.applyTransformToObject(
        hiddenObject,
        fabric.util.multiplyTransformMatrices(deltaMatrix, hiddenObject.calcTransformMatrix())
      )
      hiddenObject.setCoords()
    })
    nextColors[key] = {
      ...bucket,
      objects: hiddenObjects.map(serializeHiddenObject),
    }
  }

  fabricObject._hiddenColorsData = {
    colors: nextColors,
    lastVisibleTransform: currentMatrix,
  }
}

export const toggleFabricObjectColorVisibility = async ({
  canvas,
  object,
  color,
  visible,
  areaStampa,
}: {
  canvas: fabric.Canvas | null | undefined
  object: fabric.Object | null | undefined
  color: EditorColorType
  visible: boolean
  areaStampa: fabric.Object | null | undefined
}) => {
  const fabricObject = object as any
  const colorHex = normalizeHexColor(color?.originalValue || color?.id || color?.value || '')

  if (!canvas || !fabricObject || !colorHex) return fabricObject || null

  await syncHiddenColorsDataTransform(fabricObject)

  const hiddenData = getHiddenData(fabricObject)
  const hiddenBucket = hiddenData.colors[colorHex]
  const currentVisibleObjects = fabricObject?._objects?.length
    ? ([...fabricObject._objects] as fabric.Object[])
    : []

  if (!currentVisibleObjects.length) return fabricObject

  // Non lasciamo il logo senza oggetti visibili: altrimenti l'utente perderebbe
  // il target canvas da cui riaccendere i colori nascosti.
  if (!visible) {
    const hasObjectsToKeep = currentVisibleObjects.some(
      (child) => !objectContainsColor(child, colorHex)
    )
    const hasObjectsToHide = currentVisibleObjects.some((child) =>
      objectContainsColor(child, colorHex)
    )

    if (!hasObjectsToKeep || !hasObjectsToHide) {
      return fabricObject
    }
  }

  canvas.discardActiveObject()
  ensureStackIndexes(currentVisibleObjects)
  const nextHiddenData: HiddenColorsData = {
    colors: { ...hiddenData.colors },
  }

  if (!visible) {
    const objectsToHide = currentVisibleObjects.filter((child) =>
      objectContainsColor(child, colorHex)
    )
    const removedObjects = fabricObject.remove(...objectsToHide)

    nextHiddenData.colors[colorHex] = {
      color: colorHex,
      objects: [
        ...(nextHiddenData.colors[colorHex]?.objects || []),
        ...removedObjects.map(serializeHiddenObject),
      ],
    }
  } else {
    const restoredObjects = await enlivenSerializedObjects(hiddenBucket?.objects || [])
    fabricObject.add(...restoredObjects)
    sortByStackIndex(fabricObject._objects || []).forEach((child, index) => {
      fabricObject.moveObjectTo(child, index)
    })
    delete nextHiddenData.colors[colorHex]
  }

  nextHiddenData.lastVisibleTransform = fabricObject.calcTransformMatrix()
  fabricObject._hiddenColorsData = nextHiddenData
  applyCornerOnlyControls(fabricObject)
  fabricObject.setCoords()
  canvas.setActiveObject(fabricObject)

  // Quando un colore viene riacceso il bounding box puo' crescere. Dopo avere
  // ricreato il gruppo lo riportiamo nei limiti della print area, riducendolo
  // solo se il semplice riassestamento non basta.
  if (visible && areaStampa) {
    fabricObject.setCoords()
    let { areaBounds, objectBounds } = getObjectBoundsInAreaSpace(fabricObject, areaStampa)
    if (shrinkObjectToFitAreaBounds(fabricObject, areaBounds, objectBounds)) {
      ;({ areaBounds, objectBounds } = getObjectBoundsInAreaSpace(fabricObject, areaStampa))
    }
    const { deltaX, deltaY } = getContainmentDeltaInAreaSpace(objectBounds, areaBounds)
    translateObjectInAreaSpace(fabricObject, areaStampa, deltaX, deltaY)
    fabricObject.setCoords()
    await syncHiddenColorsDataTransform(fabricObject)
  }

  canvas.requestRenderAll()
  return fabricObject
}

export const updateHiddenSerializedColors = (
  obj: fabric.Object | null | undefined,
  originalColorHex: string,
  nextColorHex: string,
  syncOriginalToNext = false,
  pantoneReference: EditorColoreApiType[] = []
) => {
  const fabricObject = obj as any
  const normalizedOriginal = normalizeHexColor(originalColorHex)
  const normalizedNext = normalizeHexColor(nextColorHex)
  const hiddenData = getHiddenData(fabricObject)

  if (!fabricObject || !normalizedOriginal || !normalizedNext) return

  const resolveSyncedOriginalColor = (sourceHex?: string) => {
    const normalizedSource = normalizeHexColor(sourceHex || '')
    if (!syncOriginalToNext) return normalizedSource
    if (!normalizedSource) return normalizedNext
    return getPantoneHex(normalizedSource, pantoneReference)
  }

  // Gli elementi nascosti devono seguire la stessa regola del canvas visibile:
  // nei cambi manuali aggiorniamo solo lo slot originale, mentre nei
  // riallineamenti automatici (`syncOriginalToNext`) accettiamo anche il match
  // sul colore corrente per coprire i remap consecutivi di palette/tecnica.
  const shouldMatchCurrentColor = syncOriginalToNext

  const updateSerializedObject = (serialized: any) => {
    if (!serialized) return
    if (serialized.objects?.length) serialized.objects.forEach(updateSerializedObject)

    const originalFill = normalizeHexColor(serialized.originalFill || serialized.fill || '')
    const originalStroke = normalizeHexColor(serialized.originalStroke || serialized.stroke || '')
    const currentFill = normalizeHexColor(serialized.fill || '')
    const currentStroke = normalizeHexColor(serialized.stroke || '')
    const previousFillSource = serialized.originalFill || serialized.fill
    const previousStrokeSource = serialized.originalStroke || serialized.stroke

    if (
      (originalFill === normalizedOriginal ||
        (shouldMatchCurrentColor && currentFill === normalizedOriginal)) &&
      serialized.fill &&
      serialized.fill !== 'transparent'
    ) {
      serialized.fill = normalizedNext
      if (syncOriginalToNext) {
        serialized.originalFill = resolveSyncedOriginalColor(previousFillSource)
      }
    }
    if (
      (originalStroke === normalizedOriginal ||
        (shouldMatchCurrentColor && currentStroke === normalizedOriginal)) &&
      serialized.stroke &&
      serialized.stroke !== 'transparent'
    ) {
      serialized.stroke = normalizedNext
      if (syncOriginalToNext) {
        serialized.originalStroke = resolveSyncedOriginalColor(previousStrokeSource)
      }
    }
  }

  const nextColors: Record<string, HiddenColorBucket> = {}

  Object.entries(hiddenData.colors).forEach(([key, bucket]) => {
    bucket.objects?.forEach(updateSerializedObject)

    // La sidebar e i toggle hidden identificano il bucket tramite
    // `color.originalValue`. Se riallineiamo anche gli "originali" alla nuova
    // palette, dobbiamo riallineare pure la chiave del bucket nascosto.
    const nextKey = syncOriginalToNext
      ? resolveSyncedOriginalColor(bucket.color || key)
      : normalizeHexColor(bucket.color || key)

    if (!nextKey) return

    nextColors[nextKey] = {
      color: nextKey,
      objects: [
        ...(nextColors[nextKey]?.objects || []),
        ...(bucket.objects || []),
      ],
    }
  })

  fabricObject._hiddenColorsData = {
    ...hiddenData,
    colors: nextColors,
  }
}

export const applyHiddenSerializedMonochrome = (
  obj: fabric.Object | null | undefined,
  targetColorHex?: string | null,
  syncOriginalToTarget = false,
  pantoneReference: EditorColoreApiType[] = []
) => {
  const fabricObject = obj as any
  const hiddenData = getHiddenData(fabricObject)
  const normalizedTarget = targetColorHex ? normalizeHexColor(targetColorHex) : ''

  if (!fabricObject) return

  const resolveSyncedOriginalColor = (sourceHex?: string) => {
    const normalizedSource = normalizeHexColor(sourceHex || '')
    if (!syncOriginalToTarget) return normalizedSource
    if (!normalizedSource) return normalizedTarget
    return getPantoneHex(normalizedSource, pantoneReference)
  }

  // Usato sia per monocromatico sia per tecniche a colori fissi: con target
  // valorizzato forza tutti gli elementi nascosti, senza target ripristina i
  // colori originali salvati in originalFill/originalStroke.
  const updateSerializedObject = (serialized: any) => {
    if (!serialized) return
    if (serialized.objects?.length) serialized.objects.forEach(updateSerializedObject)
    const previousFillSource = serialized.originalFill || serialized.fill
    const previousStrokeSource = serialized.originalStroke || serialized.stroke

    if (serialized.fill && serialized.fill !== 'transparent') {
      serialized.fill = normalizedTarget || serialized.originalFill || serialized.fill
      if (normalizedTarget && syncOriginalToTarget) {
        serialized.originalFill = resolveSyncedOriginalColor(previousFillSource)
      }
    }

    if (serialized.stroke && serialized.stroke !== 'transparent') {
      serialized.stroke = normalizedTarget || serialized.originalStroke || serialized.stroke
      if (normalizedTarget && syncOriginalToTarget) {
        serialized.originalStroke = resolveSyncedOriginalColor(previousStrokeSource)
      }
    }
  }

  const nextColors: Record<string, HiddenColorBucket> = {}

  Object.entries(hiddenData.colors).forEach(([key, bucket]) => {
    bucket.objects?.forEach(updateSerializedObject)

    const nextKey = syncOriginalToTarget
      ? resolveSyncedOriginalColor(bucket.color || key)
      : normalizeHexColor(bucket.color || key)

    if (!nextKey) return

    nextColors[nextKey] = {
      color: nextKey,
      objects: [
        ...(nextColors[nextKey]?.objects || []),
        ...(bucket.objects || []),
      ],
    }
  })

  fabricObject._hiddenColorsData = {
    ...hiddenData,
    colors: nextColors,
  }
}

export const handleColorVisibilityChange = async ({
  canvas,
  areaStampa,
  id,
  colorId,
  visible,
  objectControls,
  loghiRefs,
  setObjectControls,
  onSyncFabricToState,
  onExportOverlayOnly,
}: {
  canvas: fabric.Canvas | null | undefined
  areaStampa: fabric.Object | null | undefined
  id: string | number
  colorId: string
  visible: boolean
  objectControls: EditorObjectType[]
  loghiRefs: { current: fabric.Object[] }
  setObjectControls: (updater: (prev: EditorObjectType[]) => EditorObjectType[]) => void
  onSyncFabricToState: (obj: fabric.Object) => void
  onExportOverlayOnly: () => void
}) => {
  if (!canvas) return

  // Handler applicativo usato dalla sidebar: aggiorna i figli del gruppo Fabric
  // e allinea il flag `hidden` nello stato dei controlli React.
  const fabricObject = canvas.getObjects().find((obj: any) => obj.customId === id)
  const control = objectControls.find((oc) => oc.id === id)
  const normalizedColorId = normalizeHexColor(colorId)
  const color = control?.colori?.find(
    (c) => normalizeHexColor(c.originalValue || c.id || c.value || '') === normalizedColorId
  )

  if (!fabricObject || !control || !color) return

  const nextObject = await toggleFabricObjectColorVisibility({
    canvas,
    object: fabricObject,
    color,
    visible,
    areaStampa,
  })

  if (!nextObject) return

  loghiRefs.current = loghiRefs.current.map((obj) =>
    (obj as any).customId === id ? nextObject : obj
  )

  let nextControlForObject: EditorObjectType | null = null
  setObjectControls((prevControls) =>
    prevControls.map((oc) => {
      if (oc.id !== id) return oc

      const nextControl = {
        ...oc,
        colori: (oc.colori || []).map((c) => {
          const sameColor =
            normalizeHexColor(c.originalValue || c.id || c.value || '') === normalizedColorId
          return sameColor ? { ...c, hidden: !visible } : c
        }),
      }
      nextControlForObject = nextControl
      ;(nextObject as any).editorObjectControl = nextControl
      return nextControl
    })
  )

  if (nextControlForObject) {
    ;(nextObject as any).editorObjectControl = nextControlForObject
  }

  onSyncFabricToState(nextObject)
  onExportOverlayOnly()
}
